import { z } from "zod"

import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  AppHeaderClassSchema,
  ContentBlocksClassSchema,
  LogoClassSchema,
  MainContainerClassSchema,
  ActivityClassSchema,
  CountryClassSchema,
  PackagingInfoHyperLinkClassSchema,
  PackagingInfoHeaderClassSchema,
  SearchEngineHeaderClassSchema,
  LastUpdateClassSchema,
  RecycledPackagingKeyInfoClassSchema,
  GenericPassportTitleClassSchema,
  OrganizationTitleClassSchema,
  ProductImageClassSchema,
  ProdcutAttributesPrimaryInformationClassSchema,
  ProdcutAttributesSecondaryInformationClassSchema,
  ProductAttributesContainerClassSchema,
  EcoscoreB2bClassSchema,
  DetailedProductCompositionContentClassSchema,
  DetailedProductCompositionTitleClassSchema,
  EcoModulationContainerClassSchema,
  EcoModulationHeaderClassSchema,
  EcoModulationBodyClassSchema,
  EcoModulationCriterionTitleClassSchema,
  EcoModulationSecondaryClassSchema,
  EcoModulationHyperLinkClassSchema,
  EcoModulationBonusIconClassSchema,
  EcoModulationMalusIconClassSchema,
} from "@tfg/types/schemas/communication-studio/classes"
import {
  CSSColorSchema,
  cssLengthSchema,
  cssLineHeightSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  textColorCssVariable,
  titleColorCSSVariable,
  contentVerticalSpacingCSSVariable,
  paddingBlockCSSVariable,
  textFontSizeCSSVariable,
  headerFontSizeCSSVariable,
  headerFontWeightCSSVariable,
  textFontWeightCSSVariable,
  paddingInlineCSSVariable,
  searchEngineTextColorCSSVariable,
  productCardBadgeBackgroundColorCSSVariable,
  productCardBadgeTextColorCSSVariable,
  headerTextDecorationCSSVariable,
  headerTextTransformCSSVariable,
  headerLineHeightCSSVariable,
  ecoscoreAlignCSSVariable,
  headerFontFamilyCSSVariable,
  logoAlignCSSVariable,
  imageAlignCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"

export const GlobalCategoryStylingParametersSchema = z.object({
  height: cssLengthSchema,
  width: cssLengthSchema,
  backgroundColor: CSSColorSchema,
  appHeader: AppHeaderClassSchema,
  fontFamily: z.string(),
  letterSpacing: cssLengthSchema,
  lineHeight: cssLineHeightSchema,
  paddingInline: paddingInlineCSSVariable,
  paddingBlock: paddingBlockCSSVariable,
  titleColor: titleColorCSSVariable,
  headerFontSize: headerFontSizeCSSVariable,
  headerTextTransform: headerTextTransformCSSVariable,
  headerFontWeight: headerFontWeightCSSVariable,
  headerTextDecoration: headerTextDecorationCSSVariable,
  headerLineHeight: headerLineHeightCSSVariable,
  headerFontFamily: headerFontFamilyCSSVariable,
  contentVerticalSpacing: contentVerticalSpacingCSSVariable,
  textFontSize: textFontSizeCSSVariable,
  textColor: textColorCssVariable,
  textFontWeight: textFontWeightCSSVariable,
  [CommunicationStudioClasses.MAIN_CONTAINER]: MainContainerClassSchema,
  [CommunicationStudioClasses.CONTENT_BLOCKS]: ContentBlocksClassSchema,
})

export const ActivitiesCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.ACTIVITY]: ActivityClassSchema,
    [CommunicationStudioClasses.COUNTRY]: CountryClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const PackagingInfoCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.PACKAGING_INFO_HEADER]:
      PackagingInfoHeaderClassSchema,
    [CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO]:
      RecycledPackagingKeyInfoClassSchema,
    [CommunicationStudioClasses.PACKAGING_INFO_HYPER_LINK]:
      PackagingInfoHyperLinkClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const SearchEngineCategoryStylingParametersSchema = z
  .object({
    backgroundColor: CSSColorSchema,
    productCardBadgeBackgroundColor: productCardBadgeBackgroundColorCSSVariable,
    productCardBadgeTextColor: productCardBadgeTextColorCSSVariable,
    searchEngineTextColor: searchEngineTextColorCSSVariable,
    fontFamily: z.string(),
    [CommunicationStudioClasses.SEARCH_ENGINE_HEADER]:
      SearchEngineHeaderClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const FooterCategoryStylingParametersSchema = z
  .object({
    marginBottom: cssLengthSchema,
    marginTop: cssLengthSchema,
    [CommunicationStudioClasses.LAST_UPDATE]: LastUpdateClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const EcoscoreCategoryStylingParametersSchema = z
  .object({
    ecoscoreAlign: ecoscoreAlignCSSVariable,
    [CommunicationStudioClasses.ECOSCORE_B2B]: EcoscoreB2bClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const LogoCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.LOGO]: LogoClassSchema,
    logoAlign: logoAlignCSSVariable,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductAttributesCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION]:
      ProdcutAttributesPrimaryInformationClassSchema,
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION]:
      ProdcutAttributesSecondaryInformationClassSchema,
    [CommunicationStudioClasses.PRODUCT_ATTRIBUTES_CONTAINER]:
      ProductAttributesContainerClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const GenericPassportTitleCategoryStylingParametersSchema = z
  .object({
    genericPassportTitle: GenericPassportTitleClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const OrganizationTitleCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.ORGANIZATION_TITLE]:
      OrganizationTitleClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const ProductImageCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.PRODUCT_IMAGE]: ProductImageClassSchema,
    imageAlign: imageAlignCSSVariable,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const DetailedProductCompositionCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_TITLE]:
      DetailedProductCompositionTitleClassSchema,
    [CommunicationStudioClasses.DETAILED_PRODUCT_COMPOSITION_CONTENT]:
      DetailedProductCompositionContentClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const EcoModulationCategoryStylingParametersSchema = z
  .object({
    [CommunicationStudioClasses.ECO_MODULATION_CONTAINER]:
      EcoModulationContainerClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_HEADER]:
      EcoModulationHeaderClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_BODY]:
      EcoModulationBodyClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_CRITERION_TITLE]:
      EcoModulationCriterionTitleClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_SECONDARY]:
      EcoModulationSecondaryClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_HYPER_LINK]:
      EcoModulationHyperLinkClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_BONUS_ICON]:
      EcoModulationBonusIconClassSchema,
    [CommunicationStudioClasses.ECO_MODULATION_MALUS_ICON]:
      EcoModulationMalusIconClassSchema,
  })
  .describe(ZodFormObjectTypeDescription.ZOD_STYLING_CLASS)

export const CategoryStylingParametersMapper = {
  [CUSTOMISATION_CATEGORIES.GLOBAL]: GlobalCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]:
    PackagingInfoCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]:
    SearchEngineCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ECOSCORE]: EcoscoreCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.LOGO]: LogoCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]:
    ProductAttributesCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]:
    GenericPassportTitleCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ORGANIZATION_TITLE]:
    OrganizationTitleCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PRODUCT_IMAGE]:
    ProductImageCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.DETAILED_PRODUCT_COMPOSITION]:
    DetailedProductCompositionCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ECO_MODULATION]:
    EcoModulationCategoryStylingParametersSchema,
}

export const StylingParametersSchema = z.object({
  [CUSTOMISATION_CATEGORIES.GLOBAL]: GlobalCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]:
    PackagingInfoCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]:
    SearchEngineCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ECOSCORE]: EcoscoreCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.LOGO]: LogoCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]:
    ProductAttributesCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ORGANIZATION_TITLE]:
    OrganizationTitleCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.PRODUCT_IMAGE]:
    ProductImageCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]:
    GenericPassportTitleCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.DETAILED_PRODUCT_COMPOSITION]:
    DetailedProductCompositionCategoryStylingParametersSchema,
  [CUSTOMISATION_CATEGORIES.ECO_MODULATION]:
    EcoModulationCategoryStylingParametersSchema,
})
